import type { Input } from "mediabunny";

import { toPresentationUs } from "../utils/time";

const disposedError = () => new DOMException("Frame timestamp lookup was disposed.", "AbortError");

/**
 * Source-wide presentation timestamps, independent of annotations or tracking.
 * AVC in MP4/MOV uses packet metadata; other formats enumerate decoded samples
 * because hidden/non-show packets need not produce a presented video frame.
 * Only timestamps are retained, never pixels or decoded sample resources.
 */
export class FrameTimestamps {
  private input: Input | undefined;
  private loading: Promise<readonly number[]> | undefined;
  private cancelLoading: (() => void) | undefined;
  private disposed = false;

  constructor(private readonly url: string) {}

  load(): Promise<readonly number[]> {
    if (this.disposed) return Promise.reject(disposedError());
    return (this.loading ??= new Promise((resolve, reject) => {
      this.cancelLoading = () => reject(disposedError());
      void this.read()
        .then(resolve, reject)
        .finally(() => {
          this.cancelLoading = undefined;
        });
    }));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelLoading?.();
    this.cancelLoading = undefined;
    this.releaseInput();
  }

  private checkDisposed(): void {
    if (this.disposed) throw disposedError();
  }

  private releaseInput(): void {
    const input = this.input;
    this.input = undefined;
    input?.dispose();
  }

  private async read(): Promise<readonly number[]> {
    // Frame stepping should not download Mediabunny until it is requested.
    const { ALL_FORMATS, EncodedPacketSink, Input, IsobmffInputFormat, UrlSource, VideoSampleSink } = await import(
      "mediabunny"
    );
    this.checkDisposed();
    const input = new Input({
      formats: ALL_FORMATS,
      source: new UrlSource(this.url, {
        maxCacheSize: 8 * 1024 * 1024,
        getRetryDelay: (attempts) => (attempts < 2 ? 0.25 : null),
      }),
    });
    this.input = input;
    try {
      const track = await input.getPrimaryVideoTrack();
      this.checkDisposed();
      if (!track) throw new Error("This source has no video track for frame stepping.");
      const [format, codec] = await Promise.all([input.getFormat(), track.getCodec()]);
      this.checkDisposed();
      const timestamps = new Set<number>();
      if (format instanceof IsobmffInputFormat && codec === "avc") {
        // AVC samples in MP4/MOV correspond to frames, but B-frame packets are
        // returned in decode order. Sort their presentation timestamps below.
        const sink = new EncodedPacketSink(track);
        for await (const packet of sink.packets(undefined, undefined, { metadataOnly: true })) {
          this.checkDisposed();
          if (Number.isFinite(packet.timestamp) && packet.timestamp >= 0) timestamps.add(packet.timestamp);
        }
      } else {
        const canDecode = await track.canDecode();
        this.checkDisposed();
        if (!canDecode) throw new Error("This browser cannot decode this video for frame stepping.");
        // This first-request fallback decodes the video once. The sink bounds
        // its predecode queue; close every sample immediately without a pixel
        // copy so decoded-frame memory stays bounded as video duration grows.
        const sink = new VideoSampleSink(track);
        for await (const sample of sink.samples()) {
          try {
            this.checkDisposed();
            if (Number.isFinite(sample.timestamp) && sample.timestamp >= 0) timestamps.add(sample.timestamp);
          } finally {
            sample.close();
          }
        }
      }
      this.checkDisposed();
      if (timestamps.size === 0) throw new Error("This video has no presentation timestamps for frame stepping.");
      return Object.freeze([...timestamps].sort((a, b) => a - b));
    } finally {
      // The index needs only numeric timestamps, so container caches can go.
      this.releaseInput();
    }
  }
}

function playableFrameCount(times: readonly number[], endTime: number): number {
  let length = times.length;
  if (Number.isFinite(endTime) && endTime > 0) {
    // Container samples can extend beyond a valid MP4 edit's playable end.
    // Bound the index without copying it, including the interior seek offset.
    let low = 0;
    let high = length;
    while (low < high) {
      const middle = low + Math.floor((high - low) / 2);
      const timestamp = times[middle]!;
      if (timestamp < endTime && frameSeekTime(timestamp) < endTime) low = middle + 1;
      else high = middle;
    }
    length = low;
  }
  return length;
}

export function boundaryFrameTime(
  times: readonly number[],
  boundary: "first" | "last",
  endTime = Infinity,
): number | undefined {
  const length = playableFrameCount(times, endTime);
  return length > 0 ? times[boundary === "first" ? 0 : length - 1] : undefined;
}

/** Step from the containing frame; compare at the browser clock's microsecond precision. */
export function adjacentFrameTime(
  times: readonly number[],
  currentTime: number,
  direction: -1 | 1,
  endTime = Infinity,
): number | undefined {
  if (!times.length || !Number.isFinite(currentTime)) return undefined;
  const length = playableFrameCount(times, endTime);
  if (length === 0) return undefined;
  const presentedUs = toPresentationUs(currentTime);
  let low = 0;
  let high = length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (toPresentationUs(times[middle]!) <= presentedUs) low = middle + 1;
    else high = middle;
  }
  const currentIndex = low - 1;
  if (currentIndex < 0) return direction === 1 ? times[0] : undefined;
  const currentUs = toPresentationUs(times[currentIndex]!);
  for (let next = currentIndex + direction; next >= 0 && next < length; next += direction) {
    const timestamp = times[next]!;
    if (toPresentationUs(timestamp) !== currentUs) return timestamp;
  }
  return undefined;
}

/** Seek just inside a frame so native microsecond truncation cannot select the preceding frame. */
export function frameSeekTime(timestamp: number): number {
  return (Math.ceil(timestamp * 1_000_000) + 1) / 1_000_000;
}
